import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { Store } from '@ngrx/store';
import { combineLatest, forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, first, map } from 'rxjs/operators';

import { getLastSectionSelector } from '../../../../menu-categories/store/menu-categories.selectors';
import { getCartItems } from '../../../../shopcart/store/shopcart.selectors';
import { ZenitEventsContextConfigs } from '../../configs/event.configs';
import * as ZenitContextHelpers from '../../helpers/context.helper';
import * as PayloadHelper from '../../helpers/payload.helper';
import {
    ItemTypes,
    ZenitEventData,
    ZenitEventName,
    ZenitEventParam,
    ZenitEventValues,
} from '../../models/zenit-general.model';
import { getGridPageProductsFiltered } from '../../store/selectors/custom.selectors';
import {
    getZenitOriginItemEventFrom,
    getZenitOriginItemListIndex,
    getZenitOriginItemListName,
    getZenitOriginItemRelatedType,
} from '../../store/selectors/zenit.selectors';

import { BasicEventParserService } from './basic-event/basic-event-parser.service';

import { CartItem } from '@api/cart/cart.models';
import { CommonWindow } from '@core/domain/models/common-window.model';
import {
    getCurrencyInfo,
    getLanguageCode,
    getLanguageLocale,
    getPage,
    getStoreId,
} from '@core/infrastructure/state/core.selectors';
import {
    getQueryResults,
    getSearchOffset,
    getSearchTerm,
    getSelectedFilters,
} from '@features/search/infrastructure/state/selectors/search.selectors';
import { getWishlistItems } from '@features/wishlist/infrastructure/state/wishlist.selectors';
import { getGridType } from '@pages/category/modules/category-grid/public_api';
import { getCategoryId, getCategoryLocationPath } from '@pages/category/store/category-page.selectors';
import { getPickupPointsViewMode } from '@pages/checkout/shipping/store/pickup/pickup.selectors';
import {
    getCategoryProduct,
    getCurrentProduct,
    getPrevRouteUrlProduct,
    getProductPageDetail,
} from '@pages/product/store/product-page.selectors';
import { getSelectedAddress } from '@shared/address/store/address/address.selectors';
import { EventFrom } from '@shared/analytics/COMMON/models/analytics-common.models';
import { getRelatedProductsPlaces } from '@shared/product/related-products/application/store/related-products.selectors';
import { getShopCartCoreId } from '@shared/shopcart-core/store/shopcart-core.selectors';
import { getActiveModalData } from '@ui/dialog/store/modal.selectors';

@Injectable()
export class ZenitEventParserService {
    constructor(
        private readonly store: Store,
        private readonly basicEventParserService: BasicEventParserService,
        @Inject(DOCUMENT) private readonly document: Document,
        @Inject(WINDOW) private readonly commonWindow: CommonWindow,
    ) {
    }

    private readonly getItemSelector = (eventName: ZenitEventName) => {
        switch (eventName) {
            case ZenitEventName.PageHit:
                return this.store.select(getProductPageDetail);
            default:
                return of(null);
        }
    };

    private readonly generateZenitSelectors = (
        config: string[],
        eventName: ZenitEventName
    ): Record<string, Observable<any>> => {
        let values: Record<string, Observable<any>> = {};

        config.forEach((param: ZenitEventParam) => {
            switch (param) {
                case 'cartId':
                    values[param] = this.store.select(getShopCartCoreId);
                    break;
                case 'items':
                    values[param] = this.store.select(getCartItems);
                    break;
                case 'categoryURL':
                    values.page = this.store.select(getPage);
                    values.productCategoryUrl = this.store.select(getPrevRouteUrlProduct);

                    values.categoryUrl = this.store.select(getCategoryLocationPath);
                    break;
                case 'categoryId':
                    values.page = this.store.select(getPage);
                    values.categoryId = this.store.select(getCategoryId);
                    values.categoryProductId = this.store.select(getCategoryProduct);
                    break;
                case 'catentryId':
                    values.item = this.getItemSelector(eventName);
                    break;
                case 'clickOrigin':
                    values.modalData = this.store.select(getActiveModalData);
                    values.originItemListIndex = this.store.select(getZenitOriginItemListIndex);
                    values.originItemListName = this.store.select(getZenitOriginItemListName);
                    values.originItemRelatedType = this.store.select(getZenitOriginItemRelatedType);
                    values.page = this.store.select(getPage);
                    values.sourceEventFromItem = this.store.select(getZenitOriginItemEventFrom);
                    break;
                case 'displayLanguage':
                    values[param] = this.store.select(getLanguageLocale);
                    break;
                case 'list':
                    values.modalData = this.store.select(getActiveModalData);
                    values.originItemListIndex = this.store.select(getZenitOriginItemListIndex);
                    values.originItemListName = this.store.select(getZenitOriginItemListName);
                    values.sourceEventFromItem = this.store.select(getZenitOriginItemEventFrom);
                    break;
                case 'listPosition':
                    values.cartItems = this.store.select(getCartItems);
                    values.filteredProductsIds = this.store.select(getGridPageProductsFiltered);
                    values.originItemListIndex = this.store.select(getZenitOriginItemListIndex);
                    values.productPageProduct = this.store.select(getCurrentProduct);
                    values.queryResults = this.store.select(getQueryResults);
                    values.relatedProductsPlaces = this.store.select(getRelatedProductsPlaces);
                    values.wishListItems = this.store.select(getWishlistItems); // Esto no debería importarse así
                    // values.wishListItems = this.wishlistPublicApi.getWishlistItems$();
                    break;
                case 'origin':
                    // Lógica en el helper
                    break;
                case 'pageType':
                    values.page = this.store.select(getPage);
                    values.isSelectedAddress = this.store.select(getSelectedAddress).pipe(
                        map(selectedAddress => !!selectedAddress),
                    );
                    values.selectedViewPickup = this.store.select(getPickupPointsViewMode);
                    break;
                case 'personalization':
                    values.relatedProductsPlaces = this.store.select(getRelatedProductsPlaces);
                    break;
                case 'section':
                    values[param] = this.store.select(getLastSectionSelector);
                    break;
                case 'skus':
                    // Lógica en el helper
                    break;
                case 'storeId':
                    values[param] = this.store.select(getStoreId);
                    break;
                case 'currency':
                    values[param] = this.store.select(getCurrencyInfo);
                    break;
                case 'zoomLevel':
                    values[param] = this.store.select(getGridType);
                    break;
                case 'searchRequestQuery':
                    values[param] = this.store.select(getSearchTerm);
                    break;
                case 'searchRequestLocale':
                    values[param] = this.store.select(getLanguageCode);
                    break;
                case 'searchRequestOffset':
                    values[param] = this.store.select(getSearchOffset);
                    break;
                case 'searchRequestFilters':
                    values[param] = this.store.select(getSelectedFilters);
                    break;
                default:
                    break;
            }
        });

        return values;
    };

    private readonly getValueForContextProperty = (
        propertyName: string,
        targetItem: ItemTypes,
        eventName: ZenitEventName,
        eventFrom: EventFrom,
        values: ZenitEventValues,
        _eventData: ZenitEventData,
        commonWindow: CommonWindow
    ): any => {
        switch (propertyName) {
            case 'brandId':
                // Valor fijo para MD
                return 11;
            case 'cartId':
                return ZenitContextHelpers.getCartId(values.cartId);
            case 'categoryId':
                return ZenitContextHelpers.getCategoryId(
                    values.page,
                    values.categoryId,
                    values.categoryProductId
                );
            case 'categoryURL':
                return ZenitContextHelpers.getCategoryURL(values);
            case 'catentryId':
                return ZenitContextHelpers.getCatentryId(targetItem);
            case 'clickOrigin':
                return ZenitContextHelpers.getClickOrigin(
                    eventFrom,
                    values.page,
                    values.modalData,
                    targetItem,
                    eventName,
                    {
                        originItemListName: values.originItemListName,
                        originItemRelatedType: values.originItemRelatedType,
                        searchOrigin: undefined,
                        sourceEventFromItem: values.sourceEventFromItem,
                    }
                );
            case 'skus':
                return ZenitContextHelpers.getSkus(_eventData?.items || values?.items);
            case 'list':
                return ZenitContextHelpers.getList(
                    eventFrom,
                    values.page,
                    values.modalData,
                    targetItem,
                    eventName,
                    {
                        originItemListName: values?.originItemListName,
                        place: _eventData?.place,
                        searchOrigin: undefined,
                        sourceEventFromItem: values?.sourceEventFromItem,
                    }
                );
            case 'listPosition':
                return ZenitContextHelpers.getProductItemIndex(
                    targetItem,
                    eventFrom,
                    values,
                    _eventData
                );
            case 'origin':
                return ZenitContextHelpers.getOrigin(
                    eventName,
                    eventFrom,
                    values.page,
                    targetItem,
                    values.modalData,
                    _eventData?.origin,
                );
            case 'pageType':
                return ZenitContextHelpers.getPageType(values, eventFrom, _eventData);
            case 'partnumber':
                return ZenitContextHelpers.getReferenceStyle(targetItem);
            case 'personalization':
                return ZenitContextHelpers.getPersonalization(
                    targetItem,
                    values?.relatedProductsPlaces,
                    _eventData?.place,
                    _eventData?.relatedsSectionId
                );
            case 'section':
                return ZenitContextHelpers.getSection(values.section);
            case 'currency':
                return ZenitContextHelpers.getCurrency(values.currency);
            case 'items':
                return ZenitContextHelpers.getItems(
                    (_eventData?.items || values?.items) as unknown as CartItem[]
                );
            case 'storeId':
                return ZenitContextHelpers.getStoreId(values.storeId);
            case 'experiments':
                return ZenitContextHelpers.getExperiments(commonWindow);
            case 'zoomLevel':
                return ZenitContextHelpers.getZoomLevel(values.zoomLevel);
            case 'searchRequestResponseTime':
            case 'searchRequestResultsNum':
            case 'searchRequestMethodName':
            case 'searchRequestLimit':
            case 'searchRequestHTTPResponseCode':
            case 'searchRequestOffset':
            case 'searchRequestProvider':
            case 'searchRequestPass':
            case 'searchRequestFilters':
                return ZenitContextHelpers.getSearchRequestParams(propertyName, values, _eventData);
            default:
                return values[propertyName];
        }
    };

    private readonly generateContextObject = (
        currentItem: ItemTypes,
        eventName: ZenitEventName,
        eventFrom: EventFrom,
        config: ZenitEventParam[],
        values: ZenitEventValues,
        _eventData: ZenitEventData,
        commonWindow: CommonWindow
    ): Record<string, any> | Array<Record<string, any>> => {
        const contextObject = {};
        const targetItem = currentItem || values?.items?.[0] || values?.item;
        config.forEach((property) => {
            try {
                contextObject[property] = this.getValueForContextProperty(
                    property,
                    targetItem,
                    eventName,
                    eventFrom,
                    values,
                    _eventData,
                    commonWindow
                );
            } catch {
                contextObject[property] = undefined;
            }
        });
        return contextObject;
    };

    /** Lógica para montar los objetos context según configuración */
    private readonly getContextObject = (
        eventName: ZenitEventName,
        eventFrom: EventFrom,
        config: ZenitEventParam[],
        values: ZenitEventValues,
        _eventData: ZenitEventData,
        commonWindow: CommonWindow,
    ): Record<string, any> | Array<Record<string, any>> => {
        if (_eventData?.items?.length) {
            return _eventData.items.map((item) => {
                return this.generateContextObject(item, eventName, eventFrom, config, values, _eventData, commonWindow);
            });
        }
        return this.generateContextObject(
            _eventData?.items?.[0],
            eventName,
            eventFrom,
            config,
            values,
            _eventData,
            commonWindow
        );
    };

    private readonly getEventContext = (
        eventName: ZenitEventName,
        eventFrom: EventFrom,
        eventData: ZenitEventData,
        commonWindow: CommonWindow,
    ): Observable<Record<string, any> | Array<Record<string, any>>> => {
        const config = ZenitEventsContextConfigs[eventName];

        const selectors = this.generateZenitSelectors(config, eventName);

        const selectorsObj: Record<string, Observable<any>> = Object.entries(selectors).reduce((acc, [key, value]) => {
            acc[key] = value.pipe(first());
            return acc;
        }, {});

        return forkJoin(selectorsObj)
            .pipe(
                map((values) => this.getContextObject(eventName, eventFrom, config, values, eventData, commonWindow)),
                catchError((error: unknown) => {
                    console.error(error);
                    return throwError(() => error);
                })
            );
    };

    public getEventObject = <T>(
        eventName: ZenitEventName,
        eventFrom?: EventFrom,
        eventData?: ZenitEventData
    ): Observable<T> => {
        return combineLatest([
            this.basicEventParserService.getBasicEvent$(eventName, eventData),
            this.getEventContext(eventName, eventFrom, eventData, this.commonWindow),
        ]).pipe(
            map(([basicData, context]) => {
                return {
                    ...basicData,
                    eventName,
                    payload: PayloadHelper.getPayload(context, this.document?.cookie),
                };
            }),
            catchError((err) => throwError(() => err))
        ) as Observable<T>;
    };
}
